import { useEffect, useState } from 'react'
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { IconRail } from './IconRail'
import { useProjectByKey, useDocument } from '../lib/queries'
import type { ProjectDto } from '../lib/types'

interface ProjectOutletContext {
  project: ProjectDto
}

const SIDEBAR_WIDTH_KEY = 'sidebar:width'
const SIDEBAR_COLLAPSED_KEY = 'sidebar:collapsed'
const SIDEBAR_MIN = 200
const SIDEBAR_MAX = 560
const SIDEBAR_DEFAULT = 280

/** /projects/:projectKey 하위 라우트 공통 layout.
 *  Sidebar 가 라우트 전환마다 unmount 되지 않도록 layout route 에서 한 번만 마운트.
 *  본문은 <Outlet /> 위치에 자식 라우트 (ProjectHome / DocumentView / DocumentEdit / DocumentVersions). */
export function ProjectLayout() {
  const { projectKey: paramProjectKey, uuid } = useParams<{ projectKey?: string; uuid?: string }>()
  const navigate = useNavigate()
  const currentDoc = useDocument(uuid)
  // /d/:uuid (짧은) 진입 시 projectKey 가 없음 → 현재 doc 의 projectKey 로 fallback
  const projectKey: string | undefined = paramProjectKey ?? currentDoc.data?.projectKey ?? undefined
  const project = useProjectByKey(projectKey)

  /** 사이드바 너비 — drag handle 로 조절. localStorage 영속. */
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const v = parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY) ?? '', 10)
    return Number.isFinite(v) && v > 0 ? Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, v)) : SIDEBAR_DEFAULT
  })
  /** mobile (≤640px) 첫 진입은 항상 collapsed — desktop localStorage 무시. */
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches) return true
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
  })
  /** toggle 시에만 transition — drag resize 중엔 transition X (성능/시각 안정) */
  const [toggling, setToggling] = useState(false)

  useEffect(() => { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth)) }, [sidebarWidth])
  useEffect(() => { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0') }, [collapsed])

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startW = sidebarWidth
    const onMove = (mv: MouseEvent) => {
      const next = startW + (mv.clientX - startX)
      setSidebarWidth(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, next)))
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const toggleSidebar = () => {
    setToggling(true)
    setCollapsed((v) => !v)
    setTimeout(() => setToggling(false), 220)
  }

  /** 사이드바 폴더 active 표시: 문서 상세 진입 시는 절대 marking 안 함 (문서는 문서, 폴더는 폴더).
   *  ProjectHome 진입 시는 ?folder=<uuid> query 가 selected folder. */
  const [searchParams] = useSearchParams()
  const selectedFolderUuid: string | null = uuid ? null : (searchParams.get('folder') ?? null)

  // project fetch 가 실패하면 not found, 진행 중이면 sidebar 자리는 비우되 layout 은 유지
  if (project.isError) {
    return <div className="container" style={{ paddingTop: 24 }}><p>Project not found.</p></div>
  }

  return (
    <div className={`split-layout${collapsed ? ' sidebar-collapsed' : ''}${toggling ? ' sidebar-toggling' : ''}`}>
      <IconRail sidebarCollapsed={collapsed} onToggleSidebar={toggleSidebar} />
      <aside className="sidebar split-sidebar" id="sidebar"
        style={{ width: collapsed ? undefined : sidebarWidth }}>
        {project.data && (
          <Sidebar
            project={project.data}
            selectedFolderUuid={selectedFolderUuid}
            selectedDocUuid={uuid}
            onSelectFolder={(folderUuid) => {
              if (folderUuid == null) navigate(`/projects/${encodeURIComponent(project.data!.projectKey)}`)
              else navigate(`/projects/${encodeURIComponent(project.data!.projectKey)}?folder=${folderUuid}`)
            }}
            onSelectDoc={(d) => navigate(`/d/${d.uuid}`)}
            onCreateDocument={(folderUuid) => navigate(`/projects/${encodeURIComponent(project.data!.projectKey)}?folder=${folderUuid}&new=doc`)}
          />
        )}
        <div className="sidebar-resize-handle" onMouseDown={startResize} aria-hidden="true" />
      </aside>
      {/* mobile dim overlay — 사이드바 펼친 상태에서 본문 위 어둡게 + 클릭으로 닫기 */}
      {!collapsed && <div className="sidebar-mobile-dim" onClick={toggleSidebar} aria-hidden />}
      {/* sidebar toggle 은 IconRail 하단 버튼이 담당 — 별도 stub X */}
      <div className="split-main">
        <Outlet context={{ project: project.data } satisfies { project: ProjectDto | undefined }} />
      </div>
    </div>
  )
}

export type { ProjectOutletContext }
